import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Spinner,
  Divider,
  useMediaQuery,
  Container,
  Badge,
  CheckIcon,
  InfoIcon,
  WarningOutlineIcon,
  Image,
  Center,
  Pressable
} from 'native-base';
import { getSurveyFormDetailsById, submitSurveyResponseAPI } from '../../api/apis/workflow';
import { Toaster } from '../../components/toaster';
import { COLORS } from '../../utils/colors';
import NoDataFound from '../../components/noDataFound';

const SurveyFormPage = () => {
  const location = useLocation();
  const [isLargerThan768] = useMediaQuery({
    base: false,
    md: true,
  });
  
  const showToast = Toaster();

  const [surveyData, setSurveyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [responses, setResponses] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [surveyAlreadySubmitted, setSurveyAlreadySubmitted] = useState(false);
  const [surveyAlreadySubmittedMessage, setSurveyAlreadySubmittedMessage] = useState('');

  // Star rating component
  const StarRating = ({ value, onChange, maxStars = 5 }) => {
    return (
      <HStack space={1} alignItems="center">
        {[...Array(maxStars)].map((_, index) => (
          <Pressable
            key={index}
            onPress={() => onChange(index + 1)}
            _pressed={{ opacity: 0.7 }}
          >
            <Text
              fontSize="4xl"
              color={index < value ? COLORS.THEME_COLOR : COLORS.BORDER_COLOR}
            >
              ★
            </Text>
          </Pressable>
        ))}
        {value > 0 && (
          <Text fontSize="sm" color={COLORS.THEME_COLOR} ml={2} fontWeight="medium">
            {value} {value === 1 ? 'star' : 'stars'}
          </Text>
        )}
      </HStack>
    );
  };

  useEffect(() => {
    const fetchSurveyData = async () => {
      try {
        const searchParams = new URLSearchParams(location.search);
        const surveyId = searchParams.get('survey_id');

        if (!surveyId) {
          showToast('Survey ID is required', 'error');
          return;
        }

        const response = await getSurveyFormDetailsById(surveyId);

        if (response.data && response.data.data.data) {
          const survey = response.data.data.data;
          setSurveyData(survey);

          // Initialize responses object with empty values for each question
          const initialResponses = {};
          const initialErrors = {};
          survey.questions?.forEach(question => {
            initialResponses[question.question_id] = '';
            initialErrors[question.question_id] = false;
          });
          setResponses(initialResponses);
          setValidationErrors(initialErrors);
        }
      } catch (error) {
        console.error('Error fetching survey data:', error);

        // Check for survey already submitted errors during fetch
        const errorMessage = error.response?.data?.message || 'Failed to load survey';
        if (error.response?.data?.message === 'This survey link has already been used' ||
          error.response?.data?.message === 'You have already submitted this survey') {
          showToast(errorMessage, 'error');
          setSurveyAlreadySubmitted(true);
          setSurveyAlreadySubmittedMessage(error.response.data.message);
        } else {
          showToast('Failed to load survey', 'error');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSurveyData();
  }, [location.search]);

  const handleResponseChange = (questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));

    // Clear validation error when user selects an option
    if (value) {
      setValidationErrors(prev => ({
        ...prev,
        [questionId]: false
      }));
    }
  };

  const clearResponses = () => {
    const initialResponses = {};
    const initialErrors = {};
    surveyData.questions?.forEach(question => {
      initialResponses[question.question_id] = '';
      initialErrors[question.question_id] = false;
    });
    setResponses(initialResponses);
    setValidationErrors(initialErrors);
    showToast('Responses cleared successfully', 'success');
  };

  const clearQuestionResponse = (questionId) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: ''
    }));
    setValidationErrors(prev => ({
      ...prev,
      [questionId]: false
    }));
  };

  const validateForm = () => {
    const errors = {};
    let hasErrors = false;

    surveyData.questions?.forEach(question => {
      if (!responses[question.question_id] || responses[question.question_id].trim() === '') {
        errors[question.question_id] = true;
        hasErrors = true;
      }
    });

    setValidationErrors(errors);
    return !hasErrors;
  };

  const handleSubmit = async () => {
    try {
      const searchParams = new URLSearchParams(location.search);
      const token = searchParams.get('token');

      // Validate all fields are answered
      if (!validateForm()) {
        showToast('All fields are required. Please answer all questions before submitting.', 'error');
        return;
      }

      setSubmitting(true);

      const payload = {
        survey_id: surveyData._id,
        answers: Object.keys(responses).map(questionId => {
          const selectedOption = responses[questionId];
          const question = surveyData.questions.find(q => q.question_id === parseInt(questionId));
          
          // Check if this is a star rating question
          const isStarRating = question?.options && question.options.some(option => 
            option.toLowerCase().includes('star')
          );
          
          let selectedAnswers = [];
          
          if (isStarRating) {
            // For star ratings, use the selected value directly
            selectedAnswers = selectedOption ? [parseInt(selectedOption)] : [];
          } else {
            // For regular options, find the option index
            const optionIndex = question?.options?.indexOf(selectedOption);
            const optionNumber = optionIndex !== -1 ? optionIndex + 1 : null;
            selectedAnswers = optionNumber ? [optionNumber] : [];
          }

          return {
            question_id: parseInt(questionId),
            selected_answers: selectedAnswers
          };
        })
      };

      const response = await submitSurveyResponseAPI(token, payload);

      if (response?.data?.data?.message) {
        showToast(response?.data?.data?.message || 'Survey submitted successfully!', 'success');
        // Clear responses after successful submission
        setResponses({});
      } else {
        // Handle specific error messages from API
        const errorMessage = response.message || 'Failed to submit survey';
        
        // Check for survey already submitted errors
        if (response.message === 'This survey link has already been used' || 
            response.message === 'You have already submitted this survey') {
          showToast(errorMessage, 'error');
          setSurveyAlreadySubmitted(true);
          setSurveyAlreadySubmittedMessage(response.message);
        } else {
          showToast(errorMessage, 'error');
        }
      }
    } catch (error) {
      // Handle network or other errors
      const errorMessage = error.response?.data?.message || 'Failed to submit survey';
      
              // Check for survey already submitted errors in catch block too
        if (error.response?.data?.message === 'This survey link has already been used' || 
            error.response?.data?.message === 'You have already submitted this survey') {
          showToast(errorMessage, 'error');
          setSurveyAlreadySubmitted(true);
          setSurveyAlreadySubmittedMessage(error.response.data.message);
        } else {
          showToast(errorMessage, 'error');
        }
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate progress
  const totalQuestions = surveyData?.questions?.length || 0;
  const answeredQuestions = Object.values(responses).filter(response => response && response.trim() !== '').length;
  const progressPercentage = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;
  const isComplete = progressPercentage === 100;

  if (loading) {
    return (
      <Box flex={1} bg={COLORS.THEME_BACKGROUND} minH="100vh" justifyContent="center" alignItems="center">
        <VStack space={4} alignItems="center">
          <Spinner size="lg" color={COLORS.THEME_COLOR} />
          <Text fontSize="lg" color={COLORS.GRAY_COLOR}>Loading survey...</Text>
        </VStack>
      </Box>
    );
  }

  if (!surveyData) {
    return (
      <Box flex={1} bg={COLORS.WHITE_COLOR} minH="100vh" justifyContent="center" alignItems="center" p={0}>
        <NoDataFound
          dataName="Oops! Survey not found"
          image={true}
          events={false}
          fullHeight={true}
          customMessage={true}
        />
      </Box>
    );
  }

  if (surveyAlreadySubmitted) {
    return (
      <Box flex={1} bg={COLORS.WHITE_COLOR} minH="100vh" justifyContent="center" alignItems="center" p={0}>
        <NoDataFound
          dataName="Oops! You have already submitted your survey"
          image={true}
          events={false}
          fullHeight={true}
          customMessage={true}
        />
      </Box>
    );
  }

  return (
    <Box flex={1} bg={COLORS.THEME_BACKGROUND} minH="100vh" py={isLargerThan768 ? 8 : 4}>
      <Container maxW="container.lg" mx="auto" px={isLargerThan768 ? 4 : 2}>
        <VStack space={6} alignItems="center">
          {/* Survey Icon */}
          <Center mb={4}>
            <Box
              bg={COLORS.WHITE_COLOR}
              borderRadius="full"
              p={4}
              boxShadow="0px 18.953px 47.382px 0px rgba(119, 115, 170, 0.10)"
            >
              <Image
                source={{ uri: 'https://cdn-icons-png.flaticon.com/512/2910/2910914.png' }}
                alt="Survey Icon"
                size="sm"
                resizeMode="contain"
              />
            </Box>
          </Center>

          {/* Header Card */}
          <Box
            w="full"
            maxW={isLargerThan768 ? "800px" : "100%"}
            bg={COLORS.WHITE_COLOR}
            borderRadius="16px"
            boxShadow="0px 18.953px 47.382px 0px rgba(119, 115, 170, 0.10)"
            p={isLargerThan768 ? 8 : 6}
            borderWidth={1}
            borderColor={COLORS.BORDER_COLOR}
          >
            <VStack space={6}>
              <Center>
                <VStack space={3} alignItems="center">
                  <Heading size={isLargerThan768 ? "xl" : "lg"} color={COLORS.BLACK_COLOR} textAlign="center">
                    {surveyData.title}
                  </Heading>
                  <HStack space={2} alignItems="center" flexWrap="wrap" justifyContent="flex-start">
                  <Heading size={isLargerThan768 ? "lg" : "md"} color={COLORS.BLACK_COLOR} textAlign="center">
                   Event : {surveyData.event_name}
                  </Heading>
                  <HStack space={3} alignItems="center" flexWrap="wrap" justifyContent="center">
                    <Badge
                      colorScheme={surveyData.type === 'event' ? 'blue' : 'green'}
                      variant="solid"
                      bg={surveyData.type === 'event' ? COLORS.BANNER_COLOR : COLORS.FILE_UPLOAD_COLOR}
                      _text={{ color: COLORS.WHITE_COLOR, fontWeight: "bold" }}
                      px={4}
                      py={2}
                      borderRadius="full"
                      >
                      {surveyData.type?.toUpperCase()}
                    </Badge>
                  </HStack>
                  </HStack>
                </VStack>
              </Center>

              <Divider bg={COLORS.SOLID_DIVIDER_COLOR} />

              <Box
                bg={COLORS.UPLOAD_BUTTON_COLOR}
                p={4}
                borderRadius="12px"
                borderLeftWidth={4}
                borderLeftColor={COLORS.THEME_COLOR}
              >
                <VStack space={4}>
                  <HStack space={3} alignItems="center">
                    <InfoIcon size="md" color={COLORS.THEME_COLOR} />
                    <Text fontSize="md" color={COLORS.BLACK_COLOR} fontWeight="medium">
                      Please answer all questions below. Your responses are important to us.
                    </Text>
                  </HStack>
                </VStack>
              </Box>
              {/* Progress Section */}
              <VStack space={3}>
                <Text fontSize="md" color={COLORS.GRAY_COLOR} fontWeight="medium" textAlign="center">
                  {answeredQuestions} of {totalQuestions} questions answered
                </Text>
                <Box w="full" bg={COLORS.BORDER_COLOR} borderRadius="full" h={3}>
                  <Box
                    bg={isComplete ? '#22C55E' : COLORS.THEME_COLOR}
                    h="full"
                    borderRadius="full"
                    w={`${progressPercentage}%`}
                    transition="width 0.3s ease"
                    boxShadow={isComplete ? "0px 2px 4px 0px #047857" : "0px 2px 4px 0px rgba(243, 112, 50, 0.2)"}
                  />
                </Box>
                <Text fontSize="sm" color={isComplete ? '#047857' : COLORS.THEME_COLOR} fontWeight="bold" textAlign="center">
                  {Math.round(progressPercentage)}% Complete
                </Text>
              </VStack>
            </VStack>
          </Box>

          {/* Questions */}
          <VStack space={4} w="full" maxW={isLargerThan768 ? "800px" : "100%"}>
            {surveyData.questions?.map((question, index) => (
              <Box
                key={question.question_id || index}
                bg={COLORS.WHITE_COLOR}
                borderRadius="16px"
                boxShadow="0px 18.953px 47.382px 0px rgba(119, 115, 170, 0.10)"
                p={isLargerThan768 ? 8 : 6}
                borderWidth={1}
                borderColor={validationErrors[question.question_id] ? COLORS.ERROR_TOAST_COLOR : COLORS.BORDER_COLOR}
                position="relative"
              >
                {validationErrors[question.question_id] && (
                  <Box
                    position="absolute"
                    top={2}
                    right={2}
                    bg={COLORS.ERROR_TOAST_COLOR}
                    borderRadius="full"
                    p={1}
                  >
                    <WarningOutlineIcon size="xs" color={COLORS.WHITE_COLOR} />
                  </Box>
                )}

                <VStack space={4}>
                  <HStack space={3} alignItems="baseline">
                    <Box
                      bg={validationErrors[question.question_id] ? COLORS.ERROR_TOAST_COLOR : COLORS.THEME_COLOR}
                      borderRadius="full"
                      w={8}
                      h={8}
                      justifyContent="center"
                      alignItems="center"
                      mt={1}
                    >
                      <Text color={COLORS.WHITE_COLOR} fontSize="sm" fontWeight="bold">
                        {index + 1}
                      </Text>
                    </Box>

                    <VStack flex={1} space={3}>
                      <HStack space={3} justifyContent="space-between" alignItems="center" position="relative">
                        <Text fontSize={isLargerThan768 ? "xl" : "lg"} fontWeight="bold" color={COLORS.BLACK_COLOR} flex={1}>
                          {question.question}
                          <Text color={COLORS.ERROR_TOAST_COLOR}> *</Text>
                        </Text>
                        {responses[question.question_id] && (
                          <Text
                            color={COLORS.THEME_COLOR}
                            fontSize="sm"
                            fontWeight="medium"
                            onTouchEnd={() => clearQuestionResponse(question.question_id)}
                            onClick={() => clearQuestionResponse(question.question_id)}
                            cursor="pointer"
                            _hover={{ opacity: 0.8 }}
                            position="absolute"
                            right={0}
                            top="50%"
                            transform="translateY(-50%)"
                          >
                            Clear
                          </Text>
                        )}
                      </HStack>
                      <VStack space={3} mt={2}>
                        {
                         (question.options && question.options.some(option => 
                           option.toLowerCase().includes('star')                         )) ? (
                          <StarRating
                            questionId={question.question_id}
                            value={responses[question.question_id] ? parseInt(responses[question.question_id]) : 0}
                            onChange={(rating) => handleResponseChange(question.question_id, rating.toString())}
                            maxStars={question.max_rating || 5}
                          />
                        ) : (
                          question.options?.map((option, optionIndex) => (
                            <HStack
                              key={optionIndex}
                              space={3}
                              alignItems="center"
                              p={4}
                              bg={responses[question.question_id] === option ? COLORS.UPLOAD_BUTTON_COLOR : 'transparent'}
                              borderRadius="12px"
                              borderWidth={2}
                              borderColor={responses[question.question_id] === option ? COLORS.THEME_COLOR : COLORS.BORDER_COLOR}
                              _pressed={{
                                bg: COLORS.UPLOAD_BUTTON_COLOR,
                                borderColor: COLORS.THEME_COLOR
                              }}
                              transition="all 0.2s ease"
                            >
                              <input
                                type="radio"
                                name={`question-${question.question_id}`}
                                value={option}
                                checked={responses[question.question_id] === option}
                                onChange={(e) => handleResponseChange(question.question_id, e.target.value)}
                                style={{
                                  margin: 0,
                                  width: '20px',
                                  height: '20px',
                                  accentColor: COLORS.THEME_COLOR,
                                  WebkitAppearance: 'none',
                                  MozAppearance: 'none',
                                  appearance: 'none',
                                  border: `2px solid ${responses[question.question_id] === option ? COLORS.THEME_COLOR : COLORS.BORDER_COLOR}`,
                                  borderRadius: '50%',
                                  backgroundColor: COLORS.WHITE_COLOR,
                                  position: 'relative',
                                  background: responses[question.question_id] === option 
                                    ? `radial-gradient(circle, ${COLORS.THEME_COLOR} 0%, ${COLORS.THEME_COLOR} 40%, ${COLORS.WHITE_COLOR} 40%, ${COLORS.WHITE_COLOR} 100%)`
                                    : COLORS.WHITE_COLOR
                                }}
                              />
                              <Text
                                fontSize="md"
                                color={COLORS.BLACK_COLOR}
                                flex={1}
                                fontWeight={responses[question.question_id] === option ? "semibold" : "normal"}
                              >
                                {option}
                              </Text>
                            </HStack>
                          ))
                        )}
                      </VStack>

                      {validationErrors[question.question_id] && (
                        <HStack space={2} alignItems="center" mt={2}>
                          <WarningOutlineIcon size="xs" color={COLORS.ERROR_TOAST_COLOR} />
                          <Text fontSize="sm" color={COLORS.ERROR_TOAST_COLOR}>
                            This question is required
                          </Text>
                        </HStack>
                      )}
                    </VStack>
                  </HStack>
                </VStack>
              </Box>
            ))}
          </VStack>

          {/* Submit Button */}
          <Box mt={6} mb={4} w="full" maxW={isLargerThan768 ? "800px" : "100%"}>
            <Center>
              <HStack space={4} alignItems="center">
                <Box
                  bg={COLORS.THEME_COLOR}
                  px={isLargerThan768 ? 12 : 8}
                  py={isLargerThan768 ? 5 : 4}
                  borderRadius="12px"
                  _pressed={{
                    bg: '#E05A2B'
                  }}
                  onTouchEnd={clearResponses}
                  onClick={clearResponses}
                  cursor="pointer"
                  boxShadow="0px 4px 12px 0px rgba(243, 112, 50, 0.3)"
                >
                  <Text color={COLORS.WHITE_COLOR} fontWeight="bold" fontSize={isLargerThan768 ? "lg" : "md"}>Clear Response</Text>
                </Box>
                <Box
                  bg={COLORS.THEME_COLOR}
                  px={isLargerThan768 ? 12 : 8}
                  py={isLargerThan768 ? 5 : 4}
                  borderRadius="12px"
                  _pressed={{
                    bg: '#E05A2B'
                  }}
                  onTouchEnd={handleSubmit}
                  onClick={handleSubmit}
                  disabled={submitting}
                  opacity={submitting ? 0.7 : 1}
                  boxShadow="0px 4px 12px 0px rgba(243, 112, 50, 0.3)"
                  cursor={submitting ? 'not-allowed' : 'pointer'}
                >
                  <HStack space={3} alignItems="center">
                    {submitting ? (
                      <Spinner size="sm" color={COLORS.WHITE_COLOR} />
                    ) : (
                      <CheckIcon size="sm" color={COLORS.WHITE_COLOR} />
                    )}
                    <Text color={COLORS.WHITE_COLOR} fontSize={isLargerThan768 ? "lg" : "md"} fontWeight="bold">
                      {submitting ? 'Submitting...' : 'Submit Survey'}
                    </Text>
                  </HStack>
                </Box>
              </HStack>
            </Center>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default SurveyFormPage; 